import * as React from 'react';
import * as PropTypes from 'prop-types';
import { clickHandler } from '../../../utils/util';
import { bindActionCreators, Dispatch } from 'redux';
import * as LoginActions from '../../../store/pages/login/actions';
import { connect } from 'react-redux';
import { Product } from '../../../store/pages/products/types';
import { InventoryAction } from '../../../store/pages/productStock/types';
import * as ct from '../../global/controls';
import * as v from '../../global/validation';
import { ValidationError } from '../../../store/global/types';
import { ApplicationState } from '../../../store';
import * as api from '../../../store/apiClient';
import Loading from '../../global/loading';
import ApiError from '../../global/apiError';

interface MappedReduxState {
    selectedVenueId: string;
    validationErrors: ValidationError[];
    products: Product[];
    productsLoading: boolean;
}

interface Actions {
    logout: () => void;
}

interface LocalProps {
    productId: string;
    productVariantId: string | null;
    currentStock: number;
    close: () => void;
}

type StockProductManualAdjustmentPanelProps = MappedReduxState & Actions & LocalProps;

interface StockProductManualAdjustmentPanelState {
    validationError: string | null;
    quantity: ct.FormValue<number | null>;
    notes: ct.FormValue<string>,
    isSaving: boolean;
    savingError: api.ApiError | null;
}

class StockProductManualAdjustmentPanel extends React.Component<StockProductManualAdjustmentPanelProps, StockProductManualAdjustmentPanelState> {
    constructor(props: StockProductManualAdjustmentPanelProps) {
        super(props);
        this.state = this.buildStateFromProps(props);
    }

    static contextTypes = {
        t: PropTypes.func
    }

    private buildStateFromProps(props: StockProductManualAdjustmentPanelProps): StockProductManualAdjustmentPanelState {
        return {
            validationError: null,
            quantity: this.validateQuantity(props.currentStock ?? null),
            notes: this.validateNotes(''),
            isSaving: false,
            savingError: null,
        };
    }

    validateQuantity = (val: number | null) => v.validate(val, 'quantity', [v.required, v.numeric], this.props.validationErrors);
    validateNotes = (val: string) => v.validate(val, 'notes', [v.required], this.props.validationErrors);

    private onFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
    }

    private saveManualAdjustment = (quantity: number, notes: string) => {
        const { selectedVenueId, productId, productVariantId, logout, close } = this.props;
        const body = {
            venueId: selectedVenueId,
            productId: productId,
            productVariantId: productVariantId,
            quantity: quantity,
            notes: notes
        }

        api.postWithAuth(`api/v1/productStock/stockAdjustment`, body, logout)
            .subscribe(
                response => this.setState({ isSaving: false, savingError: null }, close),
                (err: api.ApiError) => { this.setState({ isSaving: false, savingError: err }) });
    }

    private save = () => {
        if (!v.isValid(this.state)) {
            this.setState({ validationError: this.context.t('Global:formNotValid') });
            // TODO: Show error message!
        } else {
            this.setState({ validationError: null, isSaving: true });
            
            const { currentStock } = this.props;
            const { quantity, notes } = this.state;
            const adjustmentValue = (quantity.value ?? 0) - currentStock;
            this.saveManualAdjustment(adjustmentValue, notes.value);
        }
    }

    render() {
        const { t } = this.context;
        const { close, currentStock, productsLoading } = this.props;
        const { quantity, notes, isSaving, savingError } = this.state;

        if (isSaving || productsLoading) return <Loading />;
        if (savingError) return <ApiError error={ savingError } />;

        const hasValueChanged = currentStock !== quantity.value;

        return <div>
            <h3>{t('StockProductManualAdjustmentPanel:heading')}</h3>
            <form className='data-form' onSubmit={this.onFormSubmit} autoComplete='off'>
                <ct.NumberBox id='quantity' labelKey='StockProductManualAdjustmentPanel:adjustmentQuantity' value={quantity} callback={val => this.setState(s => ({ quantity: this.validateQuantity(val || 0) }))} />
                <ct.TextArea id='notes' rows={4}  labelKey='StockProductManualAdjustmentPanel:notes' value={notes} callback={val => this.setState(s => ({ notes: this.validateNotes(val) }))} />
                <div className='btn-toolbar' style={({ marginTop: '20px' })}>
                    <button className='btn btn-primary' disabled={!hasValueChanged} onClick={e => clickHandler(e, this.save)}>{t('Global:save')}</button>
                    <button className='btn btn-basic' onClick={e => clickHandler(e, close)}>{t('Global:cancel')}</button>
                </div>
            </form>
        </div>
    }
}

const mapStateToProps = (state: ApplicationState) => ({
    selectedVenueId: state.venues.selectedVenueId,
    validationErrors: state.products.validationErrors,
    products: state.products.products.filter(p => !p.archived),
    productsLoading: state.products.isLoading,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    logout: bindActionCreators(LoginActions.actionCreators.logout, dispatch),
});

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(StockProductManualAdjustmentPanel);
